import { useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useSuggestMetrics, useUpdateProfile } from '@/lib/auth';
import {
  ProfileUpdateRequestSchema,
  type Profile,
  type ProfileUpdateRequest,
  type SuggestMetricsResponse,
} from '@/lib/api';
import { MetricTooltip } from '@/components/MetricTooltip';
import type { MetricKey } from '@/lib/metrics-glossary';

/**
 * Formulario de edición parcial del perfil. Permite cambiar cualquier campo
 * salvo passphrase (eso vivirá en otra pantalla más adelante).
 *
 * Si el usuario cambia FTP, LTHR o cualquier T-pace, se muestra un aviso:
 * "Esto se guardará en tu histórico de métricas".
 */
export function ProfileEditForm({
  profile,
  onCancel,
  onSaved,
}: {
  profile: Profile;
  onCancel: () => void;
  onSaved: () => void;
}): ReactElement {
  const mutation = useUpdateProfile();

  const [name, setName] = useState(profile.name);
  const [age, setAge] = useState(profile.age?.toString() ?? '');
  const [sex, setSex] = useState<'' | 'M' | 'F' | 'X'>(profile.sex ?? '');
  const [weightKg, setWeightKg] = useState(profile.weightKg?.toString() ?? '');
  const [heightCm, setHeightCm] = useState(profile.heightCm?.toString() ?? '');
  const [ftpW, setFtpW] = useState(profile.ftpW?.toString() ?? '');
  const [lthrBpm, setLthrBpm] = useState(profile.lthrBpm?.toString() ?? '');
  const [tPaceRun, setTPaceRun] = useState(profile.tPaceRun ?? '');
  const [tPaceSwim, setTPaceSwim] = useState(profile.tPaceSwim ?? '');
  const [timezone, setTimezone] = useState(profile.timezone);
  const [units, setUnits] = useState<'metric' | 'imperial'>(profile.units);
  const [yearGoal, setYearGoal] = useState(profile.yearGoal ?? '');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const metricsChanged = useMemo(() => {
    return (
      String(profile.ftpW ?? '') !== ftpW ||
      String(profile.lthrBpm ?? '') !== lthrBpm ||
      (profile.tPaceRun ?? '') !== tPaceRun ||
      (profile.tPaceSwim ?? '') !== tPaceSwim
    );
  }, [profile, ftpW, lthrBpm, tPaceRun, tPaceSwim]);

  function buildPatch(): ProfileUpdateRequest {
    const numOrNull = (s: string): number | null => {
      if (s.trim() === '') return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : Number.NaN;
    };
    const intOrNull = (s: string): number | null => {
      if (s.trim() === '') return null;
      const n = Number.parseInt(s, 10);
      return Number.isFinite(n) ? n : Number.NaN;
    };
    const stringOrNull = (s: string): string | null => {
      const t = s.trim();
      return t === '' ? null : t;
    };

    return {
      name: name.trim(),
      age: intOrNull(age),
      sex: sex === '' ? null : sex,
      weightKg: numOrNull(weightKg),
      heightCm: numOrNull(heightCm),
      ftpW: intOrNull(ftpW),
      lthrBpm: intOrNull(lthrBpm),
      tPaceRun: stringOrNull(tPaceRun),
      tPaceSwim: stringOrNull(tPaceSwim),
      timezone: timezone.trim(),
      units,
      yearGoal: stringOrNull(yearGoal),
    };
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch = buildPatch();
    const parsed = ProfileUpdateRequestSchema.safeParse(patch);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data, {
      onSuccess: () => onSaved(),
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Editar perfil</h1>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Identidad</h2>
        <div className="mt-3 space-y-3">
          <FieldText id="name" label="Nombre *" value={name} onChange={setName} error={errors.name} />
          <FieldText id="age" label="Edad" value={age} onChange={setAge} type="number" error={errors.age} />
          <div>
            <label htmlFor="sex" className="block text-sm font-medium text-slate-700">
              Sexo
            </label>
            <select
              id="sex"
              value={sex}
              onChange={(e) => setSex(e.target.value as typeof sex)}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            >
              <option value="">— sin especificar —</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="X">Otro / prefiero no decir</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FieldText id="weightKg" label="Peso (kg)" value={weightKg} onChange={setWeightKg} type="number" step="0.1" error={errors.weightKg} />
            <FieldText id="heightCm" label="Talla (cm)" value={heightCm} onChange={setHeightCm} type="number" error={errors.heightCm} />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Métricas de entrenamiento
        </h2>
        {metricsChanged && (
          <p
            role="status"
            aria-live="polite"
            className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900"
          >
            Esto se guardará en tu histórico de métricas. Al guardar, también
            recalcularemos tu carga (CTL/ATL/TSB) en segundo plano si cambian
            FTP o LTHR.
          </p>
        )}

        <SuggestMetricsPanel
          onApply={(s) => {
            if (s.ftpW !== null) setFtpW(String(s.ftpW));
            if (s.lthrBpm !== null) setLthrBpm(String(s.lthrBpm));
            if (s.tPaceRun !== null) setTPaceRun(s.tPaceRun);
            if (s.tPaceSwim !== null) setTPaceSwim(s.tPaceSwim);
          }}
        />

        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <FieldText id="ftpW" label="FTP (W)" value={ftpW} onChange={setFtpW} type="number" error={errors.ftpW} metric="ftp" />
            <FieldText id="lthrBpm" label="LTHR (bpm)" value={lthrBpm} onChange={setLthrBpm} type="number" error={errors.lthrBpm} metric="lthr" />
          </div>
          <FieldText id="tPaceRun" label="T-pace running (mm:ss)" value={tPaceRun} onChange={setTPaceRun} placeholder="4:30" error={errors.tPaceRun} metric="tPaceRun" />
          <FieldText id="tPaceSwim" label="T-pace swimming (mm:ss)" value={tPaceSwim} onChange={setTPaceSwim} placeholder="1:35" error={errors.tPaceSwim} metric="tPaceSwim" />
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Preferencias</h2>
        <div className="mt-3 space-y-3">
          <FieldText id="timezone" label="Timezone" value={timezone} onChange={setTimezone} error={errors.timezone} />
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700">Unidades</legend>
            <div className="mt-1 flex gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="units" value="metric" checked={units === 'metric'} onChange={() => setUnits('metric')} />
                Métrico
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="units" value="imperial" checked={units === 'imperial'} onChange={() => setUnits('imperial')} />
                Imperial
              </label>
            </div>
          </fieldset>
          <div>
            <label htmlFor="yearGoal" className="block text-sm font-medium text-slate-700">
              Objetivo del año
            </label>
            <textarea
              id="yearGoal"
              rows={3}
              maxLength={2000}
              value={yearGoal}
              onChange={(e) => setYearGoal(e.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
          </div>
        </div>
      </section>

      {mutation.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo guardar el perfil. Revisa los campos.
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
}

/**
 * Botón "💡 Sugerir desde mis entrenos" + panel inline con las sugerencias
 * recibidas del backend.
 *
 * - Click en el botón → `refetch()` (manual, sin auto-load al montar).
 * - Mientras carga, muestra spinner inline.
 * - Si todos los valores son null → mensaje "no tienes datos suficientes".
 * - Si al menos uno no es null → tabla con los valores + botones Aplicar/Descartar.
 *
 * El componente no muta el formulario directamente: comunica con el padre
 * vía `onApply`. Esto evita acoplar el panel con el estado del form.
 */
function SuggestMetricsPanel({
  onApply,
}: {
  onApply: (s: SuggestMetricsResponse) => void;
}): ReactElement {
  const suggestQuery = useSuggestMetrics(30);
  const [dismissed, setDismissed] = useState(false);

  const data = suggestQuery.data;
  // Si el usuario ha descartado las últimas sugerencias o aún no ha pulsado,
  // ocultamos el panel.
  const showPanel = data !== undefined && !dismissed;

  function handleClick() {
    setDismissed(false);
    void suggestQuery.refetch();
  }

  const allNull =
    data !== undefined &&
    data.ftpW === null &&
    data.lthrBpm === null &&
    data.tPaceRun === null &&
    data.tPaceSwim === null;

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <button
        type="button"
        onClick={handleClick}
        disabled={suggestQuery.isFetching}
        className="rounded-md border border-teal-600 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {suggestQuery.isFetching
          ? 'Calculando…'
          : '💡 Sugerir desde mis entrenos'}
      </button>

      {suggestQuery.isError && (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          No se pudieron calcular las sugerencias. Inténtalo de nuevo.
        </p>
      )}

      {showPanel && data !== undefined && allNull && (
        <p role="status" className="mt-2 text-xs text-slate-700">
          Aún no tienes datos suficientes. Conecta Strava y sincroniza varias
          semanas primero.
        </p>
      )}

      {showPanel && data !== undefined && !allNull && (
        <div className="mt-3 space-y-2" data-testid="suggest-metrics-panel">
          <p className="text-xs text-slate-700">
            Basado en <strong>{data.basedOnActivities}</strong> actividades de
            los últimos <strong>{data.basedOnDays}</strong> días.
          </p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <li>
              <span className="text-slate-500">FTP:</span>{' '}
              <strong className="text-slate-900">
                {data.ftpW !== null ? `${data.ftpW} W` : '—'}
              </strong>
            </li>
            <li>
              <span className="text-slate-500">LTHR:</span>{' '}
              <strong className="text-slate-900">
                {data.lthrBpm !== null ? `${data.lthrBpm} bpm` : '—'}
              </strong>
            </li>
            <li>
              <span className="text-slate-500">T-pace run:</span>{' '}
              <strong className="text-slate-900">
                {data.tPaceRun ?? '—'}
              </strong>
            </li>
            <li>
              <span className="text-slate-500">T-pace swim:</span>{' '}
              <strong className="text-slate-900">
                {data.tPaceSwim ?? '—'}
              </strong>
            </li>
          </ul>
          <p className="text-[11px] text-slate-500">{data.methodology}</p>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                onApply(data);
                setDismissed(true);
              }}
              className="rounded-md bg-teal-600 px-3 py-1 text-xs font-medium text-white hover:bg-teal-700"
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Descartar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldText({
  id,
  label,
  value,
  onChange,
  type = 'text',
  step,
  placeholder,
  error,
  metric,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
  error?: string;
  /** Clave del glosario M2.32.E — añade icono "?" junto al label. */
  metric?: MetricKey;
}): ReactElement {
  const errorId = `${id}-error`;
  return (
    <div>
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
        {metric && <MetricTooltip metric={metric} iconOnly />}
      </div>
      <input
        id={id}
        type={type}
        step={step}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error !== undefined}
        aria-describedby={error ? errorId : undefined}
        className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-rose-700">
          {error}
        </p>
      )}
    </div>
  );
}
